"use client";

import { useTruncatedElement } from "@/components/expandableSection/useTruncatedElement";
import Button from "@/components/design-system/button/button";
import { DeviceType } from "@/types/search";
import { useWindowWidth } from "@react-hook/window-size";
import CaretDownIcon from "public/next-public/common/icons/caret-solid-down.svg";
import { useEffect, useRef, useState } from "react";
import styles from "./expandableSection.module.scss";

export type ExpandableSectionProps = {
  children: React.ReactNode;
  collapsedSize?: number;
  lineHeight?: number;
  numVisibleLines?: number;
  className?: string;
};

export default function ExpandableSection({
  children,
  collapsedSize,
  lineHeight = 20,
  numVisibleLines = 3,
  className,
}: ExpandableSectionProps) {
  const calculatedCollapsedSize = collapsedSize ?? (numVisibleLines + 1) * lineHeight;
  const expandableContentRef = useRef<HTMLDivElement>(null);
  const { isTruncated, isShowingLess, toggleIsShowingMore } = useTruncatedElement(
    expandableContentRef,
    calculatedCollapsedSize,
  );
  const windowWidth = useWindowWidth();
  const [deviceType, setDeviceType] = useState<DeviceType>("desktop");

  const contentStyle: React.CSSProperties = isShowingLess
    ? { maxHeight: calculatedCollapsedSize + "px" }
    : { maxHeight: "unset" };

  useEffect(() => {
    let newDeviceType: DeviceType;
    if (windowWidth < 576) newDeviceType = "mobile";
    else if (windowWidth < 992) newDeviceType = "tablet";
    else newDeviceType = "desktop";
    if (newDeviceType !== deviceType) setDeviceType(newDeviceType);
  }, [windowWidth, deviceType]);

  function handleClickBody(): void {
    if (isTruncated && deviceType !== "desktop") {
      toggleIsShowingMore();
    }
  }

  return (
    <div className={styles.expandableSectionSection}>
      <div className={styles.textWrapper}>
        <div
          ref={expandableContentRef}
          className={[styles.expandableContent, className].filter(Boolean).join(" ")}
          style={contentStyle}
          onClick={handleClickBody}
        >
          {children}
        </div>
        {isTruncated && isShowingLess && <div className={styles.gradientOverlay} />}
      </div>

      {isTruncated && (
        <div className={styles.readMoreContainer}>
          <Button
            variant="tertiary"
            className={styles.readMoreLink}
            onClick={toggleIsShowingMore}
            text={isShowingLess ? "Read More" : "Read Less"}
            showLeftIcon={false}
            showRightIcon={true}
            rightIcon={
              <CaretDownIcon
                width={16}
                height={16}
                aria-hidden
                className={[styles.caretIcon, !isShowingLess ? styles.expanded : ""].filter(Boolean).join(" ")}
              />
            }
          />
        </div>
      )}
    </div>
  );
}
